import React, { useCallback, useMemo, useEffect } from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import Subscription, {
  SubscriptionLayout,
} from '../../components/Subscription';

import { subscriptionSelectors } from '../../machines';
import { SubscriptionProduct, SubscriptionStatus } from '../../models';

import {
  useComponentSettings,
  useFormatSubscriptionDetails,
  useSubscriptionStatus,
} from '../../hooks';

import { INACTIVE_STATE, PAYMENT_ISSUES } from './constants';
import {
  useSubscriptionAvailableActions,
  useSubscriptionDetailsService,
} from './hooks';
import SubscriptionManagerLoader from '../../components/SubscriptionManagerLoader/SubscriptionManagerLoader';
import { Currency } from '@chargify/formatters';

export type SubscriptionContainerProps = {
  /**
   * Subscription identifer
   */
  id: number;
  /**
   * Show subscription details event handler
   */
  onShowDetails?: (subscriptionId: number) => void;
  /**
   * Subscription layout
   */
  layout?: SubscriptionLayout;
  /**
   * Determines whether details are visible
   */
  detailsVisible: boolean;
  /**
   * Subscription product
   */
  product: SubscriptionProduct;
  /**
   * Subscription status
   */
  status: SubscriptionStatus;
  /**
   * Subscription currency
   */
  currency: Currency;
  /**
   * Procut family identifer
   */
  productFamilyId: number;
  /**
   * Subscription components settings
   */
  components: { updatesAllowed: boolean; usedInBilling: boolean };
};

const SubscriptionContainer = ({
  id,
  onShowDetails,
  layout = 'expandable-card',
  detailsVisible,
  product,
  status,
  components,
  currency,
  productFamilyId,
}: SubscriptionContainerProps) => {
  const {
    componentsAllocation: { editAllowed: customerSelfService },
  } = useComponentSettings();

  const { t, i18n } = useTranslation(['common']);

  const subscriptionDetailsService = useSubscriptionDetailsService(id);

  const {
    billing,
    customFields,
    referralCode,
    threeDsActionLink,
    isLoading,
    ...restSubscriptionDetails
  } = useSelector(
    subscriptionDetailsService,
    subscriptionSelectors.getSubscriptionDetails
  );

  useEffect(() => {
    if (detailsVisible) {
      subscriptionDetailsService.send('GET_SUBSCRIPTION_DETAILS');
    }
  }, [subscriptionDetailsService, detailsVisible]);

  const subscriptionTag = useSubscriptionStatus(status);

  const getCardColor = useCallback(
    (state: SubscriptionStatus) =>
      INACTIVE_STATE.includes(state) ? 'gray' : 'white',
    []
  );

  const formattedSubscriptionDetails = useFormatSubscriptionDetails({
    ...restSubscriptionDetails,
    currency,
    product,
    status,
    locale: i18n.language,
    t,
  });

  const totalPrice = useMemo(() => {
    const formattedPrice = formattedSubscriptionDetails.totalPrice;
    if (formattedPrice && !['trial', 'cancelled'].includes(status)) {
      return {
        label: formattedPrice,
        color: PAYMENT_ISSUES.includes(status) ? 'danger.dark' : 'success.main',
      };
    }
    return null;
  }, [formattedSubscriptionDetails.totalPrice, status]);

  const renderComponentsSection =
    (components.updatesAllowed && customerSelfService) ||
    components.usedInBilling;

  const { subscription } = useComponentSettings();
  const availableActions = useSubscriptionAvailableActions(
    { status },
    subscription.scopes
  );

  return (
    <>
      {isLoading ? (
        <SubscriptionManagerLoader />
      ) : (
        <Subscription
          id={id}
          name={product.name}
          catalogId={productFamilyId}
          description={product.description}
          statusTag={subscriptionTag}
          status={status}
          layout={layout}
          price={totalPrice}
          detailsVisible={detailsVisible}
          onShowDetails={() => onShowDetails && onShowDetails(id)}
          cardColor={getCardColor(status)}
          billingDetails={billing}
          renderComponentsSection={renderComponentsSection}
          renderCustomFieldsSection={customFields?.visible}
          availableActions={availableActions}
          referralCode={referralCode}
          threeDsActionLink={threeDsActionLink}
          currency={restSubscriptionDetails.currency}
          coupons={restSubscriptionDetails.coupons}
          subscriptionDetailsService={subscriptionDetailsService}
          {...formattedSubscriptionDetails}
        />
      )}
    </>
  );
};

export default SubscriptionContainer;
